/**
 * React Query hook for Users
 * Provides automatic caching, background sync, and optimistic updates
 */

import { useQuery } from '@tanstack/react-query';
import { totalCountEvent } from 'services/generalServices';
import { listUsers } from 'services/teamManagement.service';
import { queryKeys } from '../../../config/queryClient';

/**
 * Fetches all active users
 *
 * @param {Object} options - Query options
 * @param {string} options.status - Filter by status (default: 'ACTIVE')
 * @param {boolean} options.excludeFromLoading - Exclude from global loading state
 * @returns {Promise<Array>} - Array of users
 */
const fetchUsers = async ({ status = 'ACTIVE', excludeFromLoading = false } = {}) => {
  // Get total count first
  const countResponse = await totalCountEvent('user');
  const pageLimit = countResponse.data;

  // Fetch users
  const response = await listUsers(status, pageLimit, '', 'forward', '', '', false, true, false, false, true, true);

  return response.data?.firm_users || [];
};

/**
 * Hook to fetch users with React Query
 *
 * @param {Object} options - Configuration options
 * @param {string} options.status - Filter by status (default: 'ACTIVE')
 * @param {boolean} options.excludeFromLoading - Exclude from global loading state (default: false)
 * @param {boolean} options.enabled - Enable/disable query (default: true)
 *
 * @returns {Object} - userList, isUserLoading, error, refetch
 */
export const useUsersQuery = ({ status = 'ACTIVE', excludeFromLoading = false, enabled = true } = {}) => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.users.list(status),
    queryFn: () => fetchUsers({ status, excludeFromLoading }),
    staleTime: 10 * 60 * 1000, // Consider data stale after 10 minutes
    cacheTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus for relatively static data
    retry: 2,
    enabled, // Allow disabling the query
  });

  return {
    userList: data || [],
    isUserLoading: isLoading,
    error,
    refetch,
  };
};
